import { VueQueryPlugin } from "@tanstack/vue-query";
import { queryClient } from "@/lib/query";
import { createPinia } from "pinia";
import { createApp } from "vue";
import type { RouteLocationNormalized, Router } from "vue-router";
import App from "./App.vue";
import "./assets/index.css";
import router from "./router";

import PrimeVue from "primevue/config";
import "primeicons/primeicons.css";

import Breadcrumb from "primevue/breadcrumb";
import Button from "primevue/button";
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup";
import DataTable from "primevue/datatable";
import DatePicker from "primevue/datepicker";
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import FileUpload from "primevue/fileupload";
import FloatLabel from "primevue/floatlabel";
import IftaLabel from "primevue/iftalabel";
import Image from "primevue/image";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Menu from "primevue/menu";
import RadioButton from "primevue/radiobutton";
import Row from "primevue/row";
import Password from "primevue/password";
import Popover from "primevue/popover";
import ScrollPanel from "primevue/scrollpanel";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import MultiSelect from "primevue/multiselect";
import Skeleton from "primevue/skeleton";
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import Tag from "primevue/tag";
import Textarea from "primevue/textarea";
import Timeline from "primevue/timeline";
import Toast from "primevue/toast";
import ToggleSwitch from "primevue/toggleswitch";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";
import { humanInterval } from "./utils/datetime";
import { pluralize } from "./utils/string";
import { Preset } from "./PrimeVuePreset";
import { installOverlayFocus } from "@/lib/helpers/overlayFocus";
import { initTheme } from "./composables/ui/useTheme";

// Before the first paint, so a light user never sees a frame of the dark default.
initTheme();

const app = createApp(App);

app.directive("tooltip", Tooltip);

app.component("Breadcrumb", Breadcrumb);
app.component("Button", Button);
app.component("Card", Card);
app.component("Checkbox", Checkbox);
app.component("Column", Column);
app.component("ColumnGroup", ColumnGroup);
app.component("DataTable", DataTable);
app.component("DatePicker", DatePicker);
app.component("Dialog", Dialog);
app.component("FloatLabel", FloatLabel);
app.component("IftaLabel", IftaLabel);
app.component("Image", Image);
app.component("InputGroup", InputGroup);
app.component("InputGroupAddon", InputGroupAddon);
app.component("InputNumber", InputNumber);
app.component("InputText", InputText);
app.component("Password", Password);
app.component("Popover", Popover);
app.component("Textarea", Textarea);
app.component("Menu", Menu);
app.component("RadioButton", RadioButton);
app.component("Row", Row);
app.component("ScrollPanel", ScrollPanel);
app.component("Tabs", Tabs);
app.component("TabList", TabList);
app.component("Tab", Tab);
app.component("TabPanels", TabPanels);
app.component("TabPanel", TabPanel);
app.component("Tag", Tag);
app.component("Toast", Toast);
app.component("ToggleSwitch", ToggleSwitch);
app.component("Timeline", Timeline);
app.component("Skeleton", Skeleton);
app.component("Divider", Divider);
app.component("FileUpload", FileUpload);
app.component("Select", Select);
app.component("SelectButton", SelectButton);
app.component("MultiSelect", MultiSelect);

const pinia = createPinia();
pinia.use(({ store }) => {
  store.router = router as Router & { currentRoute: RouteLocationNormalized };
});

app.config.globalProperties.$pl = pluralize;
app.config.globalProperties.$humanInterval = humanInterval;

app.use(pinia);
app.use(router);
app.use(VueQueryPlugin, { queryClient });
app.use(PrimeVue, {
  theme: {
    preset: Preset,
    options: {
      darkModeSelector: "none",
    },
  },
});
app.use(ToastService);
installOverlayFocus();

app.mount("#app");

declare module "pinia" {
  export interface PiniaCustomProperties {
    router: Router & { currentRoute: RouteLocationNormalized }; // Declare the router type
  }
}

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    $pl: typeof pluralize;
    $humanInterval: typeof humanInterval;
  }
}
